// Phase 1A Step 35 — Section 02 SIKA.
// New landing section, not in handoff/landing-reference (handoff predates SIKA).
// Source of truth: Every Document/SIKA.md §1 (what SIKA is), §15A (the headline
// transformation), §2 (tone), §4 (goal categories), §10 (SIKA Pro tier).
//
// Position in flow: Section 02, between Hero (01, implicit) and AgentSwarm (03).
// SIKA is the conversational layer above the agent swarm — the goal-to-plan
// front door. Section 03 onwards explains the engine that executes the plan.

import { SIKA_TURNS } from "./sika-data.ts";
import { SikaConversation } from "./SikaConversation.tsx";

export function SikaSection(): JSX.Element {
  return (
    <section
      id="sika"
      className="sz-l-section sz-l-sika"
      style={{
        position: "relative",
        padding: "160px 0",
        borderTop: "1px solid var(--border)",
        background: "linear-gradient(180deg, #0C0B08 0%, #0A0A0A 100%)",
      }}
    >
      <div className="kente-bg" />
      <div className="container" style={{ position: "relative" }}>
        <div
          className="sz-l-section-header sz-l-sika-header"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1.6fr",
            gap: 80,
            marginBottom: 60,
          }}
        >
          <div>
            <span className="bracket">Section 02 · SIKA</span>
          </div>
          <div>
            <h2
              className="serif"
              style={{
                fontSize: "clamp(40px, 5vw, 68px)",
                fontWeight: 500,
                lineHeight: 1.02,
                color: "var(--text)",
                marginBottom: 22,
                letterSpacing: "-0.01em",
              }}
            >
              Tell SIKA what you want.
              <br />
              <em style={{ color: "var(--accent)", fontStyle: "italic" }}>
                Your agents build the path.
              </em>
            </h2>
            <p
              style={{
                fontSize: 17,
                color: "var(--text-muted)",
                maxWidth: 640,
                lineHeight: 1.55,
                marginBottom: 14,
              }}
            >
              SIKA is the conversational layer above the swarm — your goal-to-plan agent. Type a
              real-world target in plain language. SIKA returns a ROLLOVER chain, market mix, and
              honest timeline. The agents execute it.
            </p>
            <p
              className="mono"
              style={{
                fontSize: 11,
                color: "var(--text-dim)",
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                marginTop: 18,
              }}
            >
              · The Akan word for gold, money, wealth.
            </p>
          </div>
        </div>

        {/* Two-column body: example chips on left, conversation card on right */}
        <div
          className="sz-l-sika-body"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1.6fr",
            gap: 80,
            alignItems: "start",
          }}
        >
          {/* Left column — what SIKA recognises */}
          <div>
            <div
              className="mono eyebrow-muted"
              style={{
                marginBottom: 18,
              }}
            >
              What SIKA recognises
            </div>
            <ul
              style={{
                listStyle: "none",
                padding: 0,
                margin: 0,
                display: "flex",
                flexDirection: "column",
                gap: 14,
              }}
            >
              {EXAMPLE_PROMPTS.map((p, i) => (
                <li
                  key={i}
                  style={{
                    display: "flex",
                    gap: 14,
                    alignItems: "flex-start",
                    padding: "14px 16px",
                    border: "1px solid var(--border)",
                    background: "var(--surface)",
                  }}
                >
                  <span
                    className="mono"
                    style={{
                      fontSize: 9,
                      letterSpacing: "0.18em",
                      color: "var(--accent)",
                      textTransform: "uppercase",
                      flex: "0 0 auto",
                      marginTop: 3,
                    }}
                  >
                    {p.cat}
                  </span>
                  <span style={{ fontSize: 14, color: "var(--text)", lineHeight: 1.45 }}>
                    {p.text}
                  </span>
                </li>
              ))}
            </ul>
            <p
              style={{
                marginTop: 22,
                fontSize: 13,
                color: "var(--text-muted)",
                lineHeight: 1.55,
              }}
            >
              Plain language in. Mathematical plan out. SIKA refines as you push back — faster
              timelines, smaller stakes, different markets.
            </p>
          </div>

          {/* Right column — live conversation card */}
          <div>
            <SikaConversation />
            <p
              className="mono"
              style={{
                fontSize: 11,
                color: "var(--text-dim)",
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                marginTop: 18,
                textAlign: "right",
              }}
            >
              / Cycling {SIKA_TURNS.length} live examples · You bring the goal · SIKA does the math.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

// Static prompt examples shown on the left rail. Not animated — these
// communicate SIKA's range. Categories per SIKA.md §4.
const EXAMPLE_PROMPTS: ReadonlyArray<{ cat: string; text: string }> = [
  { cat: "A", text: "I want to buy a MacBook." },
  { cat: "A", text: "I want the new iPhone." },
  { cat: "B", text: "I want to buy land in East Legon." },
  { cat: "B", text: "I want to build a house." },
  { cat: "C", text: "Hajj for my mother next year." },
  { cat: "E", text: "Cover one term of school fees." },
];
